import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Copy, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIGenerator = () => {
  const [contentType, setContentType] = useState('email');
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/ai/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.generations || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/ai/generate`,
        { type: contentType, prompt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedContent(res.data.content);
      toast.success('Content generated successfully!');
      fetchHistory();
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success('Content copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const contentTypes = [
    { value: 'email', label: 'Email Campaign' },
    { value: 'linkedin', label: 'LinkedIn Message' },
    { value: 'cold_email', label: 'Cold Email' },
    { value: 'blog', label: 'Blog Post' },
    { value: 'ad_copy', label: 'Ad Copy' },
    { value: 'social_media', label: 'Social Media Post' },
    { value: 'seo_article', label: 'SEO Article' },
    { value: 'landing_page', label: 'Landing Page Copy' },
  ];

  return (
    <div data-testid="ai-generator-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-2">AI Content Generator</h1>
        <p className="text-lg text-muted-foreground">Generate marketing content powered by Advanced AI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Generator Form */}
        <Card data-testid="generator-form-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-emerald-500" />
              Generate Content
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Content Type</label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger data-testid="content-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value} data-testid={`content-type-${type.value}`}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Your Prompt</label>
              <Textarea
                data-testid="prompt-input"
                placeholder="Example: Write an email campaign for a new SaaS product launch targeting B2B companies..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                className="resize-none"
              />
            </div>

            <Button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Generated Output */}
        <Card data-testid="generated-output-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Generated Content</CardTitle>
          </CardHeader>
          <CardContent>
            {generatedContent ? (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg min-h-[300px] max-h-[500px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-body text-sm">{generatedContent}</pre>
                </div>
                <Button
                  data-testid="copy-btn"
                  onClick={handleCopy}
                  variant="outline"
                  className="w-full"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy to Clipboard
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-center">
                <div>
                  <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Your generated content will appear here</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card data-testid="history-card">
        <CardHeader>
          <CardTitle className="text-2xl font-heading">Recent Generations</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <div className="space-y-3">
              {history.slice(0, 5).map((gen, index) => (
                <motion.div
                  key={gen.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`history-item-${index}`}
                  className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => setGeneratedContent(gen.content)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium capitalize">{gen.type.replace('_', ' ')}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(gen.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{gen.prompt}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No generations yet. Start creating!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIGenerator;
